import { EmailTemplateType } from '@/api/entities/email-template';
import { RoleName } from '@/api/entities/user-role-type';
import { VehiculeCheckProgressStatus } from '@/api/entities/vehicule-check';
import { TFunction } from 'i18next';

export const API_BASE_URL = import.meta.env.VITE_API_URL;
export const TOKEN_API_PUBLIC = import.meta.env.VITE_TOKEN_API_PUBLIC;
export const R2_PUBLIC_DOMAIN = import.meta.env.VITE_R2_PUBLIC_DOMAIN;

export const LOCAL_BASE_URL = 'http://localhost:3000';
export const DEV_BASE_URL = 'https://api.dev.lucius-ai.com';
export const PREPROD_BASE_URL = 'https://api.preprod.lucius-ai.com';
export const PROD_BASE_URL = 'https://api.lucius-ai.com';

export const isLocal = API_BASE_URL === LOCAL_BASE_URL;
export const isDev = API_BASE_URL === DEV_BASE_URL;
export const isPreprod = API_BASE_URL === PREPROD_BASE_URL;
export const isProd = API_BASE_URL === PROD_BASE_URL;

export const MODAL_SIZE = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
};

export const ICON_SIZE = {
  DEFAULT: 'default',
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
};

export const MODE_DRAW = {
  RECTANGLE: 'RECTANGLE',
  POLYGON: 'POLYGON',
};

const VEHICULE_CHECK_TYPE = {
  PROFESSIONAL: 'PROFESSIONAL',
  INDIVIDUAL: 'INDIVIDUAL',
};

export const getOptionsTypeFilter = (t: TFunction<undefined>) => [
  { label: t('common:usage'), value: '0' },
  { label: t('common:distributor'), value: '1' },
  { label: t('common:branch'), value: '2' },
  { label: t('common:distributorAndBranch'), value: '3' },
];

export const getOptionsUserRoleFilter = (t: TFunction<undefined>) => [
  { label: t('common:allRoles'), value: '' },
  { label: t('common:groupManager'), value: RoleName.GROUP_MANAGER },
  {
    label: t('common:companyManager'),
    value: RoleName.COMPANY_MANAGER,
  },
  {
    label: t('common:branchManager'),
    value: RoleName.BRANCH_MANAGER,
  },
  { label: t('common:branchAgent'), value: RoleName.BRANCH_AGENT },
];

export const getOptionsStatusFilter = (t: TFunction<undefined>) => [
  { label: t('common:allStatuses'), value: '' },
  { label: t('common:active'), value: 'true' },
  { label: t('common:inactive'), value: 'false' },
];

export const getOptionsStatusFilterV2 = (t: TFunction<undefined>) => [
  { label: t('common:allStates'), value: '' },
  { label: t('common:active'), value: 'true' },
  { label: t('common:inactive'), value: 'false' },
];

export const getOptionsVehiculeCheckType = (t: TFunction<undefined>) => [
  { label: t('common:proAndIndividual'), value: '' },
  { label: t('common:professional'), value: VEHICULE_CHECK_TYPE.PROFESSIONAL },
  { label: t('common:individual'), value: VEHICULE_CHECK_TYPE.INDIVIDUAL },
];

export const getOptionsCheckpointType = (t: TFunction<undefined>) => [
  { label: t('common:allTypes'), value: '' },
  { label: t('common:photo'), value: 'PHOTO' },
  { label: t('common:video'), value: 'VIDEO' },
];

export const getOptionProVehiculeCheckStatus = (t: TFunction<undefined>) => [
  { label: t('common:allStatuses'), value: '' },
  // {
  //   label: t('inspection:inspectionInProgress'),
  //   value: VehiculeCheckProgressStatus.IN_PROGRESS,
  // },
  // {
  //   label: t('common:inspectionCompleted'),
  //   value: VehiculeCheckProgressStatus.COMPLETED,
  // },
  {
    label: `${t('common:inspectionCompleted')} - ${t('inspection:receiptInProgress')}`,
    value: VehiculeCheckProgressStatus.COMPLETED_STILL_WAITING_FOR_VIDEO,
  },
  {
    label: `${t('common:inspectionCompleted')} - ${t('inspection:processInProgress')}`,
    value: VehiculeCheckProgressStatus.COMPLETED_STILL_PROCESSING_VIDEO,
  },
  {
    label: `${t('common:inspectionCompleted')} - ${t('inspection:reportAvailable')}`,
    value: VehiculeCheckProgressStatus.COMPLETED_REPORT_AVAILABLE,
  },
];

export const getOptionIndividualVehiculeCheckStatus = (
  t: TFunction<undefined>,
) => [
  { label: t('common:allStatuses'), value: '' },
  {
    label: t('inspection:requestCreated'),
    value: VehiculeCheckProgressStatus.REQUEST_CREATED,
  },
  {
    label: t('inspection:requestCreatedAndSent'),
    value: VehiculeCheckProgressStatus.REQUEST_CREATED_AND_SENT,
  },
  // {
  //   label: t('inspection:inspectionInProgress'),
  //   value: VehiculeCheckProgressStatus.IN_PROGRESS,
  // },
  // {
  //   label: t('common:inspectionCompleted'),
  //   value: VehiculeCheckProgressStatus.COMPLETED,
  // },
  {
    label: `${t('common:inspectionCompleted')} - ${t('inspection:receiptInProgress')}`,
    value: VehiculeCheckProgressStatus.COMPLETED_STILL_WAITING_FOR_VIDEO,
  },
  {
    label: `${t('common:inspectionCompleted')} - ${t('inspection:processInProgress')}`,
    value: VehiculeCheckProgressStatus.COMPLETED_STILL_PROCESSING_VIDEO,
  },
  {
    label: `${t('common:inspectionCompleted')} - ${t('inspection:reportAvailable')}`,
    value: VehiculeCheckProgressStatus.COMPLETED_REPORT_AVAILABLE,
  },
];

export const getOptionsReportVersion = (
  t: TFunction<undefined>,
  defaultValue: string,
) => [
  {
    label: t('inspection:initVersion'),
    value: '0',
    selected: defaultValue === '0',
  },
  {
    label: t('inspection:moderatorVersion'),
    value: '1',
    selected: defaultValue === '1',
  },
  {
    label: t('inspection:clientVersion'),
    value: '2',
    selected: defaultValue === '2',
  },
];

export const NOTIFY = {
  SUCCESS: 'success',
  WARNING: 'warning',
  ERROR: 'error',
  INFO: 'info',
};

export const getEmailTemplateTypeOptions = (
  t: TFunction<undefined>,
  placeholder?: string,
) => [
  { label: placeholder || t('common:allTypes'), value: '' },
  {
    label: t('common:accountCreation'),
    value: EmailTemplateType.ACCOUNT_CREATION,
  },
  {
    label: t('common:forgotPassword'),
    value: EmailTemplateType.FORGOT_PASSWORD,
  },
  {
    label: t('common:inspectionRequest'),
    value: EmailTemplateType.INSPECTION_REQUEST,
  },
  {
    label: t('common:other'),
    value: EmailTemplateType.OTHER,
  },
];

// export const optionsElement = [
//   { label: 'Aile arrière droite', value: '0', color: '#9640d4' },
//   { label: 'Aile arrière gauche', value: '1', color: '#ffc565' },
//   { label: 'Aile avant droite', value: '2', color: '#5793c5' },
//   { label: 'Aile avant gauche', value: '3', color: '#bc43c7' },
//   { label: 'Antenne', value: '4', color: '#1e2ad1' },
//   { label: 'Anti-brouillard arrière droite', value: '5', color: '#ac1865' },
//   { label: 'Anti-brouillard arrière gauche', value: '6', color: '#803025' },
//   { label: 'Anti-brouillard arrière milieu', value: '7', color: '#e8f986' },
//   { label: 'Anti-brouillard avant droite', value: '8', color: '#6ef86c' },
//   { label: 'Anti-brouillard avant gauche', value: '9', color: '#bec6cf' },
//   { label: 'Appuie-tête arrière droit', value: '10', color: '#308d9e' },
//   { label: 'Appuie-tête arrière gauche', value: '11', color: '#b35634' },
//   { label: 'Appuie-tête arrière milieu', value: '12', color: '#0aa184' },
//   { label: 'Appuie-tête avant droit', value: '13', color: '#c8cbb0' },
//   { label: 'Appuie-tête avant gauche', value: '14', color: '#81f860' },
//   {
//     label: 'Appuie-tête siège 3ème range droit',
//     value: '15',
//     color: '#1cfa27',
//   },
//   {
//     label: 'Appuie-tête siège 3ème range gauche',
//     value: '16',
//     color: '#076a17',
//   },
//   { label: 'Assise banquette arrière', value: '17', color: '#4b8c45' },
//   { label: 'Assise siège 3ème rangée droit', value: '18', color: '#2ecccd' },
//   { label: 'Assise siège 3ème rangée gauche', value: '19', color: '#035ad3' },
//   { label: 'Assise siège arrière droit', value: '20', color: '#d5c36d' },
//   { label: 'Assise siège arrière gauche', value: '21', color: '#2958b9' },
//   { label: 'Assise siège arrière milieu', value: '22', color: '#dc304b' },
//   { label: 'Assise siège avant droit', value: '23', color: '#5d7524' },
//   { label: 'Assise siège avant gauche', value: '24', color: '#8dc881' },
//   { label: 'Autre élément extérieur', value: '25', color: '#b93ff8' },
//   { label: 'Baie de pare-brise', value: '26', color: '#f671f3' },
//   { label: 'Banquette arrière assise', value: '27', color: '#f4537c' },
//   { label: 'Banquette arrière dossier', value: '28', color: '#212f97' },
//   { label: 'Barre de toit droite', value: '29', color: '#f69289' },
//   { label: 'Barre de toit gauche', value: '30', color: '#d0a090' },
//   { label: 'Bas de caisse droit', value: '31', color: '#c76123' },
//   { label: 'Bas de caisse gauche', value: '32', color: '#5b8c0b' },
//   { label: 'Becquet', value: '33', color: '#5cf0e9' },
//   { label: 'Bouton', value: '34', color: '#83615c' },
//   { label: 'Boîtier rétroviseur droit', value: '35', color: '#0f2719' },
//   { label: 'Boîtier rétroviseur gauche', value: '36', color: '#170b85' },
//   { label: 'Cable de recharge lente', value: '37', color: '#26f9b3' },
//   { label: 'Cable de recharge rapide', value: '38', color: '#613814' },
//   { label: 'Cache moyeu', value: '39', color: '#892d61' },
//   { label: 'Cache écrou', value: '40', color: '#96c106' },
//   { label: 'Cache-bagage', value: '41', color: '#94b992' },
//   { label: 'Calandre', value: '42', color: '#4f8937' },
//   { label: 'Caméra parking arrière', value: '43', color: '#083586' },
//   { label: 'Capot', value: '44', color: '#a8bdec' },
//   { label: "Carnet d'entretien", value: '45', color: '#769dbe' },
//   { label: 'Carte SD', value: '46', color: '#41b316' },
//   { label: 'Casquette tableau de bord', value: '47', color: '#408f12' },
//   { label: 'Catadioptre arrière droite', value: '48', color: '#280cad' },
//   { label: 'Catadioptre arrière gauche', value: '49', color: '#025e42' },
//   { label: 'Ceinture 3ème rangée droite', value: '50', color: '#555a54' },
//   { label: 'Ceinture 3ème rangée gauche', value: '51', color: '#025223' },
//   { label: 'Ceinture arrière droite', value: '52', color: '#1a5284' },
//   { label: 'Ceinture arrière gauche', value: '53', color: '#4b0daf' },
//   { label: 'Ceinture arrière milieu', value: '54', color: '#3a9b07' },
//   { label: 'Ceinture avant droite', value: '55', color: '#e38ee8' },
//   { label: 'Ceinture avant gauche', value: '56', color: '#331209' },
//   { label: "Certificat d'immatriculation", value: '57', color: '#42e737' },
//   { label: 'Ciel de toit', value: '58', color: '#7f4d95' },
//   { label: 'Clignotant côté', value: '59', color: '#685ad0' },
//   { label: 'Clé', value: '60', color: '#791b29' },
//   { label: 'Coin de panneau arrière droit', value: '61', color: '#17b5e9' },
//   { label: 'Coin de panneau arrière gauche', value: '62', color: '#5f2508' },
//   { label: 'Coin de pare-choc arrière droit', value: '63', color: '#bb4897' },
//   { label: 'Coin de pare-choc arrière gauche', value: '64', color: '#888f07' },
//   { label: 'Commande de vitre', value: '65', color: '#3a6fdf' },
//   { label: 'Commande frein de parking', value: '66', color: '#64430a' },
//   { label: 'Comodo droit', value: '67', color: '#292af9' },
//   { label: 'Comodo gauche', value: '68', color: '#30bf96' },
//   { label: 'Contrôle technique', value: '69', color: '#b378de' },
//   { label: 'Coque rétroviseur droit', value: '70', color: '#00fc8e' },
//   { label: 'Coque rétroviseur gauche', value: '71', color: '#660d10' },
//   { label: 'Cric', value: '72', color: '#7bf57b' },
//   { label: "Crochet d'attelage", value: '73', color: '#0267c7' },
//   { label: 'Dossier siège 3ème rangée droit', value: '74', color: '#e03f00' },
//   { label: 'Dossier siège 3ème rangée gauche', value: '75', color: '#817389' },
//   { label: 'Dossier siège arrière droit', value: '76', color: '#619382' },
//   { label: 'Dossier siège arrière gauche', value: '77', color: '#a7c65d' },
//   { label: 'Dossier siège arrière milieu', value: '78', color: '#14f0da' },
//   { label: 'Dossier siège avant droit', value: '79', color: '#611781' },
//   { label: 'Dossier siège avant gauche', value: '80', color: '#b510d0' },
//   { label: 'Echappement', value: '81', color: '#767366' },
//   { label: 'Enjoliveur', value: '82', color: '#d324a2' },
//   { label: 'Essuie-glace arrière', value: '83', color: '#103415' },
//   { label: 'Essuie-glace avant', value: '84', color: '#b97017' },
//   { label: 'Feu de jour avant droit', value: '85', color: '#ab0da2' },
//   { label: 'Feu de jour avant gauche', value: '86', color: '#5ea0c6' },
//   { label: 'Feu stop central', value: '87', color: '#8dd779' },
//   { label: 'Fléchettes', value: '88', color: '#df3da0' },
//   { label: 'Garniture couvercle arrière', value: '89', color: '#aab9cc' },
//   {
//     label: 'Garniture de lunette arrière droite',
//     value: '90',
//     color: '#72eb56',
//   },
//   {
//     label: 'Garniture de lunette arrière gauche',
//     value: '91',
//     color: '#4c20d5',
//   },
//   { label: 'Garniture de porte arrière droite', value: '92', color: '#036097' },
//   { label: 'Garniture de porte arrière gauche', value: '93', color: '#ae6432' },
//   { label: 'Garniture de porte avant droite', value: '94', color: '#c492d8' },
//   { label: 'Garniture de porte avant gauche', value: '95', color: '#9cc2d9' },
//   {
//     label: 'Garniture de porte de malle arrière droite',
//     value: '96',
//     color: '#cd5079',
//   },
//   {
//     label: 'Garniture de porte de malle arrière gauche',
//     value: '97',
//     color: '#908747',
//   },
//   { label: 'Garniture intérieur', value: '98', color: '#a4e3cb' },
//   {
//     label: 'Garniture intérieur coffre côté droit',
//     value: '99',
//     color: '#a4ed19',
//   },
//   {
//     label: 'Garniture intérieur coffre côté gauche',
//     value: '100',
//     color: '#f03d31',
//   },
//   { label: 'Glace rétroviseur droit', value: '101', color: '#8296a5' },
//   { label: 'Glace rétroviseur gauche', value: '102', color: '#03b63d' },
//   { label: 'Grille de pare-choc droite', value: '103', color: '#296e5f' },
//   { label: 'Grille de pare-choc gauche', value: '104', color: '#185b08' },
//   { label: 'Grille de pare-choc milieu', value: '105', color: '#4bfa7a' },
//   {
//     label: 'Grilles ventilation arrière droite',
//     value: '106',
//     color: '#06bede',
//   },
//   {
//     label: 'Grilles ventilation arrière gauche',
//     value: '107',
//     color: '#23ace1',
//   },
//   {
//     label: 'Grilles ventilation arrière milieu',
//     value: '108',
//     color: '#2c1bc4',
//   },
//   { label: 'Grilles ventilation avant droite', value: '109', color: '#82ba09' },
//   { label: 'Grilles ventilation avant gauche', value: '110', color: '#0918ea' },
//   { label: 'Grilles ventilation avant milieu', value: '111', color: '#f9fa4c' },
//   {
//     label: 'Grilles ventilation avant milieu droite',
//     value: '112',
//     color: '#7c6a98',
//   },
//   {
//     label: 'Grilles ventilation avant milieu gauche',
//     value: '113',
//     color: '#c2e0c0',
//   },
//   { label: 'Habitacle', value: '114', color: '#6d1153' },
//   { label: 'Hayon', value: '115', color: '#b18c5c' },
//   { label: 'Jante alu biton polychrome', value: '116', color: '#917176' },
//   { label: 'Jante alu standard', value: '117', color: '#65a957' },
//   { label: 'Jante tôle acier', value: '118', color: '#5839f4' },
//   { label: 'Joint de coffre', value: '119', color: '#5c3707' },
//   { label: 'Joint de porte arrière droite', value: '120', color: '#aaa232' },
//   { label: 'Joint de porte arrière droite', value: '121', color: '#d1b5b6' },
//   { label: 'Joint de porte arrière gauche', value: '122', color: '#0a20b4' },
//   { label: 'Joint de porte arrière gauche', value: '123', color: '#9d19c8' },
//   { label: 'Joint de porte avant droite', value: '124', color: '#44dbac' },
//   { label: 'Joint de porte avant droite', value: '125', color: '#84d0b3' },
//   { label: 'Joint de porte avant gauche', value: '126', color: '#a87f9a' },
//   { label: 'Joint de porte avant gauche', value: '127', color: '#52d549' },
//   { label: 'Jupe de pare-choc arrière', value: '128', color: '#325efc' },
//   { label: 'Kit réparation (compresseur)', value: '129', color: '#8d5e6c' },
//   { label: 'Kit réparation (recharge)', value: '130', color: '#c7cc39' },
//   { label: 'Kit sécurité', value: '131', color: '#d7d85b' },
//   { label: 'Levier de vitesses', value: '132', color: '#d69e19' },
//   { label: 'Levier de vitre', value: '133', color: '#8c39dc' },
//   { label: 'Lunette arrière', value: '134', color: '#9ff00a' },
//   { label: 'Manuel du véhicule', value: '135', color: '#745058' },
//   { label: 'Monogramme marque arrière', value: '136', color: '#052793' },
//   { label: 'Monogramme marque avant', value: '137', color: '#c3ed0e' },
//   { label: 'Monogramme modèle arrière', value: '138', color: '#de8d8b' },
//   { label: 'Montant de pare-brise droit', value: '139', color: '#f5473e' },
//   { label: 'Montant de pare-brise gauche', value: '140', color: '#052223' },
//   { label: 'Montant de toit droit', value: '141', color: '#16be58' },
//   { label: 'Montant de toit gauche', value: '142', color: '#201f7d' },
//   { label: 'Montant milieu droit', value: '143', color: '#50a281' },
//   { label: 'Montant milieu gauche', value: '144', color: '#c6381c' },
//   { label: "Moulure d'aile arrière droite", value: '145', color: '#f7dbd5' },
//   { label: "Moulure d'aile arrière gauche", value: '146', color: '#91750e' },
//   { label: "Moulure d'aile avant droite", value: '147', color: '#68052c' },
//   { label: "Moulure d'aile avant gauche", value: '148', color: '#df60ed' },
//   { label: 'Moulure de hayon', value: '149', color: '#e7ed7b' },
//   { label: 'Moulure de pare-choc arrière', value: '150', color: '#ee36b5' },
//   {
//     label: 'Moulure de pare-choc arrière droite',
//     value: '151',
//     color: '#afce51',
//   },
//   {
//     label: 'Moulure de pare-choc arrière gauche',
//     value: '152',
//     color: '#216024',
//   },
//   { label: 'Moulure de pare-choc avant droit', value: '153', color: '#099efb' },
//   {
//     label: 'Moulure de pare-choc avant gauche',
//     value: '154',
//     color: '#957c9c',
//   },
//   { label: 'Moulure de porte arrière droite', value: '155', color: '#c2c70a' },
//   { label: 'Moulure de porte arrière gauche', value: '156', color: '#3cb524' },
//   { label: 'Moulure de porte avant droite', value: '157', color: '#f4c388' },
//   { label: 'Moulure de porte avant gauche', value: '158', color: '#6b11de' },
//   {
//     label: 'Moulure de porte de malle arrière droite',
//     value: '159',
//     color: '#7d4b15',
//   },
//   {
//     label: 'Moulure de porte de malle arrière droite',
//     value: '160',
//     color: '#ef3d0d',
//   },
//   { label: 'Optique arrière droit', value: '161', color: '#7666aa' },
//   {
//     label: 'Optique arrière droit (côté coffre)',
//     value: '162',
//     color: '#e5565e',
//   },
//   { label: 'Optique arrière gauche', value: '163', color: '#4c69d0' },
//   {
//     label: 'Optique arrière gauche (côté coffre)',
//     value: '164',
//     color: '#77d0ca',
//   },
//   { label: 'Optique avant droit', value: '165', color: '#07906a' },
//   { label: 'Optique avant gauche', value: '166', color: '#e8c425' },
//   { label: 'Panneau arrière droit', value: '167', color: '#54d4c7' },
//   { label: 'Panneau arrière gauche', value: '168', color: '#da3f6d' },
//   { label: 'Panneau avant droit', value: '169', color: '#d2747b' },
//   { label: 'Panneau avant gauche', value: '170', color: '#48ad64' },
//   { label: 'Pare-brise', value: '171', color: '#57ebbf' },
//   { label: 'Pare-choc arrière', value: '172', color: '#33ffcb' },
//   { label: 'Pare-choc avant', value: '173', color: '#d3da75' },
//   { label: 'Pare-choc avant droit', value: '174', color: '#94141b' },
//   { label: 'Pare-choc avant gauche', value: '175', color: '#fba663' },
//   { label: 'Pare-soleil avant droit', value: '176', color: '#79d657' },
//   { label: 'Pare-soleil avant gauche', value: '177', color: '#93ea54' },
//   { label: 'Passage de roue arrière droit', value: '178', color: '#acf2ab' },
//   { label: 'Passage de roue arrière gauche', value: '179', color: '#dedcfa' },
//   { label: 'Passage de roue avant droit', value: '180', color: '#91e98e' },
//   { label: 'Passage de roue avant gauche', value: '181', color: '#898322' },
//   { label: 'Pavillon', value: '182', color: '#df4570' },
//   { label: 'Pige à pneu', value: '183', color: '#4f92f4' },
//   { label: 'Planche de bord', value: '184', color: '#209dbe' },
//   { label: "Plaque d'immatriculation arrière", value: '185', color: '#15a51a' },
//   { label: "Plaque d'immatriculation avant", value: '186', color: '#d352df' },
//   { label: 'Pneu', value: '187', color: '#b241fd' },
//   { label: 'Poignée de porte arrière droite', value: '188', color: '#07f212' },
//   { label: 'Poignée de porte arrière gauche', value: '189', color: '#eee83b' },
//   { label: 'Poignée de porte avant droite', value: '190', color: '#055a40' },
//   { label: 'Poignée de porte avant gauche', value: '191', color: '#938c73' },
//   {
//     label: 'Poignée de porte de malle arrière droite',
//     value: '192',
//     color: '#aa2b93',
//   },
//   {
//     label: 'Poignée de porte de malle arrière gauche',
//     value: '193',
//     color: '#061b81',
//   },
//   { label: 'Porte arrière droite', value: '194', color: '#68c4c5' },
//   { label: 'Porte arrière gauche', value: '195', color: '#5c5d6d' },
//   { label: 'Porte avant droite', value: '196', color: '#f50df9' },
//   { label: 'Porte avant gauche', value: '197', color: '#c38ea9' },
//   { label: 'Porte coulissante droite', value: '198', color: '#79b5fe' },
//   { label: 'Porte coulissante gauche', value: '199', color: '#0c10e8' },
//   { label: 'Porte de malle arrière droite', value: '200', color: '#0009f4' },
//   { label: 'Porte de malle arrière gauche', value: '201', color: '#fd123a' },
//   { label: 'Protection sous moteur', value: '202', color: '#72c701' },
//   { label: 'Pédale de conduite', value: '203', color: '#4b3dde' },
//   { label: 'Radar de parking', value: '204', color: '#4398ea' },
//   { label: 'Roue de secours', value: '205', color: '#b7ea5f' },
//   { label: 'Réglettes', value: '206', color: '#e2828d' },
//   { label: 'Rétroviseur central', value: '207', color: '#87f48c' },
//   { label: 'Seuil de chargement', value: '208', color: '#cea73e' },
//   { label: 'Soubassement', value: '209', color: '#fddd89' },
//   { label: 'Spoiler de pare-choc avant', value: '210', color: '#7439c3' },
//   { label: 'Spoiler de pare-chocs', value: '211', color: '#248584' },
//   { label: "Tableau d'instruments", value: '212', color: '#fb5a37' },
//   { label: 'Tapis arrière', value: '213', color: '#98dc9e' },
//   { label: 'Tapis arrière droite', value: '214', color: '#55aaa1' },
//   { label: 'Tapis arrière gauche', value: '215', color: '#8df360' },
//   { label: 'Tapis avant droit', value: '216', color: '#7f5809' },
//   { label: 'Tapis avant gauche', value: '217', color: '#75d58d' },
//   { label: 'Tapis de coffre', value: '218', color: '#dd3485' },
//   { label: 'Trappe à carburant', value: '219', color: '#7152ad' },
//   {
//     label: 'Vitre (porte côté) arrière droite',
//     value: '220',
//     color: '#27533d',
//   },
//   {
//     label: 'Vitre (porte côté) arrière gauche',
//     value: '221',
//     color: '#8191a8',
//   },
//   { label: 'Vitre (porte côté) avant droite', value: '222', color: '#725426' },
//   { label: 'Vitre (porte côté) avant gauche', value: '223', color: '#ca53bb' },
//   {
//     label: 'Vitre (porte de malle) porte droite',
//     value: '224',
//     color: '#c68838',
//   },
//   {
//     label: 'Vitre (porte de malle) porte gauche',
//     value: '225',
//     color: '#fa6e7e',
//   },
//   { label: 'Vitre custode arrière droite', value: '226', color: '#0848a0' },
//   { label: 'Vitre custode arrière gauche', value: '227', color: '#ba03b7' },
//   { label: 'Vitre custode avant droite', value: '228', color: '#6fac8f' },
//   { label: 'Vitre custode avant gauche', value: '229', color: '#f40fb8' },
//   { label: 'Vitre de pavillon', value: '230', color: '#0ccd03' },
//   { label: 'Volant', value: '231', color: '#6c69eb' },
//   { label: 'Écran central', value: '232', color: '#04498c' },
// ];

// export const optionsDmgList = [
//   { label: 'Bosse(s)', value: 'Bosse(s)' },
//   { label: 'Bosse(s) et Griffes', value: 'Bosse(s) et Griffes' },
//   { label: 'Cassé', value: 'Cassé' },
//   { label: 'Crevé', value: 'Crevé' },
//   { label: 'Déchiré', value: 'Déchiré' },
//   { label: 'Eclat(s)', value: 'Eclat(s)' },
//   { label: 'Griffe', value: 'Griffe' },
//   { label: 'Hernie', value: 'Hernie' },
//   { label: 'Sale', value: 'Sale' },
//   { label: 'Stickage', value: 'Stickage' },
//   { label: 'Trou', value: 'Trou' },
//   { label: 'Usé', value: 'Usé' },
//   { label: 'Pneu Déchiré', value: 'Pneu Déchiré' },
//   { label: 'Corrosion', value: 'Corrosion' },
//   { label: 'Déformé', value: 'Déformé' },
//   { label: 'Dégraffé', value: 'Dégraffé' },
//   { label: 'Manquant', value: 'Manquant' },
//   { label: 'Mauvaise réparation', value: 'Mauvaise réparation' },
//   { label: 'Non Conforme', value: 'Non Conforme' },
//   { label: 'Défaut / autre défaut', value: 'Défaut / autre défaut' },
// ];

// export const optionsDamages = [
//   { label: 'Bosse(s)_1', value: '0', color: '#FF5733' },
//   { label: 'Cassé_1', value: '1', color: '#33FF57' },
//   { label: 'Crevé_1', value: '2', color: '#3357FF' },
//   { label: 'Déchiré_1', value: '3', color: '#FF33A1' },
//   { label: 'Eclat(s)_1', value: '4', color: '#FF8C33' },
//   { label: 'Griffe_1', value: '5', color: '#33FFF3' },
//   { label: 'Hernie_1', value: '6', color: '#A833FF' },
//   { label: 'Sale_1', value: '7', color: '#FFC733' },
//   { label: 'Stickage_1', value: '8', color: '#33FFA5' },
//   { label: 'Trou_1', value: '9', color: '#8C33FF' },
//   { label: 'Usé_1', value: '10', color: '#FF333F' },
//   { label: 'Pneu Déchiré_1', value: '11', color: '#33FF8C' },
//   { label: 'Bosse(s) et Griffes_1', value: '12', color: '#E651BC' },
//   { label: 'Corrosion_1', value: '13', color: '#9C81A2' },
//   { label: 'Déformé_1', value: '14', color: '#04D819' },
//   { label: 'Dégraffé_1', value: '15', color: '#5B74A9' },
//   { label: 'Manquant_1', value: '16', color: '#0052BE' },
//   { label: 'Mauvaise réparation_1', value: '17', color: '#870A54' },
//   { label: 'Non Conforme_1', value: '18', color: '#AEFECB' },
//   { label: 'Bosse(s)_2', value: '19', color: '#FF5733' },
//   { label: 'Cassé_2', value: '20', color: '#33FF57' },
//   { label: 'Crevé_2', value: '21', color: '#3357FF' },
//   { label: 'Déchiré_2', value: '22', color: '#FF33A1' },
//   { label: 'Eclat(s)_2', value: '23', color: '#FF8C33' },
//   { label: 'Griffe_2', value: '24', color: '#33FFF3' },
//   { label: 'Hernie_2', value: '25', color: '#A833FF' },
//   { label: 'Sale_2', value: '26', color: '#FFC733' },
//   { label: 'Stickage_2', value: '27', color: '#33FFA5' },
//   { label: 'Trou_2', value: '28', color: '#8C33FF' },
//   { label: 'Usé_2', value: '29', color: '#FF333F' },
//   { label: 'Pneu Déchiré_2', value: '30', color: '#33FF8C' },
//   { label: 'Bosse(s) et Griffes_2', value: '31', color: '#E651BC' },
//   { label: 'Corrosion_2', value: '32', color: '#9C81A2' },
//   { label: 'Déformé_2', value: '33', color: '#04D819' },
//   { label: 'Dégraffé_2', value: '34', color: '#5B74A9' },
//   { label: 'Manquant_2', value: '35', color: '#0052BE' },
//   { label: 'Mauvaise réparation_2', value: '36', color: '#870A54' },
//   { label: 'Non Conforme_2', value: '37', color: '#AEFECB' },
//   { label: 'Bosse(s)_3', value: '38', color: '#FF5733' },
//   { label: 'Cassé_3', value: '39', color: '#33FF57' },
//   { label: 'Crevé_3', value: '40', color: '#3357FF' },
//   { label: 'Déchiré_3', value: '41', color: '#FF33A1' },
//   { label: 'Eclat(s)_3', value: '42', color: '#FF8C33' },
//   { label: 'Griffe_3', value: '43', color: '#33FFF3' },
//   { label: 'Hernie_3', value: '44', color: '#A833FF' },
//   { label: 'Sale_3', value: '45', color: '#FFC733' },
//   { label: 'Stickage_3', value: '46', color: '#33FFA5' },
//   { label: 'Trou_3', value: '47', color: '#8C33FF' },
//   { label: 'Usé_3', value: '48', color: '#FF333F' },
//   { label: 'Pneu Déchiré_3', value: '49', color: '#33FF8C' },
//   { label: 'Bosse(s) et Griffes_3', value: '50', color: '#E651BC' },
//   { label: 'Corrosion_3', value: '51', color: '#9C81A2' },
//   { label: 'Déformé_3', value: '52', color: '#04D819' },
//   { label: 'Dégraffé_3', value: '53', color: '#5B74A9' },
//   { label: 'Manquant_3', value: '54', color: '#0052BE' },
//   { label: 'Mauvaise réparation_3', value: '55', color: '#870A54' },
//   { label: 'Non Conforme_3', value: '56', color: '#AEFECB' },
// ];

export const damageLevelOptions = [
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
];

export const REPORT_STATUS = {
  A_MODERER: 'A_MODERER',
  MODERE: 'MODERE',
};

export const CORRECTION_TYPE = {
  AJOUT: 'AJOUT',
  MODIFICATION: 'MODIFICATION',
  SUPPRESSION: 'SUPPRESSION',
};

export const LANGUAGE = [
  {
    lang: 'FR',
    flag: 'france',
  },
  {
    lang: 'EN',
    flag: 'united-states',
  },
];

export const COMPONENT_KEY = {
  FRONT_VIEW: 'Photos de face du véhicule',
  REAR_VIEW: 'Vue arrière',
  '34_FR_VIEW': '3/4 Vue de face',
  FR_WHEEL_PICTURE: 'Roue de devant',
  '34_RR_VIEW': '3/4 vue arrière',
  '34_RL_VIEW': '3/4 vue arrière gauche',
  '34_FL_VIEW': '3/4 vue avant gauche',
  CONDITION_BUMPER_FRONT: 'Pare choc avant',
  RR_WHEEL_PICTURE: 'Roue arrière droite',
  RL_WHEEL_PICTURE: 'Roue arrière gauche',
  ROOF_VIEW: 'Toit',
  FL_WHEEL_PICTURE: 'Roue avant gauche',
  ENGINE_COMPARTMENT_PICTURE: 'Compartiment du moteur',
  TRUNK_PICTURE: 'Coffre',
  DASHBOARD_AFTER_START_PICTURE: 'Tableaux de bord après img démarrage',
  FL_SEAT_PICTURE: 'Siège avant gauche',
  STEERING_WHEEL_PICTURE: 'Volant',
  RR_SEAT_PICTURE: 'Siège arrière droite',
  FL_DOOR_PANEL_PICTURE: 'Panneau de porte avant gauche',
  RL_DOOR_PANEL_PICTURE: 'Panneau de porte arrière gauche',
  CENTER_CONSOLE_PICTURE: 'Console centrale',
  RR_DOOR_PANEL_PICTURE: 'Panneau de porte arrière droite',
  DASHBOARD_PICTURE: "Tableaux d'instrument",
  RL_SEAT_PICTURE: 'Siège arrière gauche',
  FR_SEAT_PICTURE: 'Siège avant droite',
  KEY_SETS_PICTURE: 'Clés',
  REGISTRATION_CERTIFICATE_PICTURE: "Certificat d'immatriculation",
  SERVICE_BOOK_PICTURES: "Carnet d'entretien",
  FR_DOOR_PANEL_PICTURE: 'Panneau de porte avant droite',
  TECHNICAL_CONTROL_PICTURE: 'Contrôle technique',
  USER_MANUAL_PICTURE: 'Manuel utilisateur',
  RIGHT_SIDE_VIEW: 'Vue côté droite',
  LEFT_SIDE_VIEW: 'Vue côté gauche',
  VEHICLE_UNDERBODY_PICTURE_REAR: 'Dessous de véhicule arrière',
  VEHICLE_UNDERBODY_PICTURE_FRONT: 'Dessous de véhicule avant',
  VEHICLE_UNDERBODY_PICTURE_OTHER: 'Dessous de véhicule autres',
  ADMINISTRATIVE_STATUS_CERTIFICATE_PICTURE:
    'Certificat de situation administrative',
  VIN_VEHICLE_CHASSIS_PICTURES: 'Vin',
  MAINTENANCE_BILLS_PICTURES: 'Facture des maintenances',
  RR_TIRE_WEAR_PICTURE: 'Usure des pneus arrière droite',
  FL_TIRE_WEAR_PICTURE: 'Usure des pneux avant gauche',
  RL_TIRE_WEAR_PICTURE: 'Usure des pneux arrière gauche',
  FR_TIRE_WEAR_PICTURE: 'Usure des pneux avant droite',
  DASHBOARD_MILEAGE_PICTURE: 'Tableau de bord kilométrique',
  CERTIFICATE_OF_CONFORMITY_PICTURE: 'Certificat de conformité',
  REGISTRATION_CERTIFICATE_PICTURE_AUTO1: "Certificata d'immatriculation_auto1",
  MILEAGE_PICTURE_TEST_DRIVE_BEFORE: 'Kilométrage essai routier avant',
  BEDS_PICTURES: 'Lit de véhicule',
  MOTORHOME_OTHER_PICTURE: 'Camping car autre photos',
  KITCHEN_AREA_PICTURE: 'Espace de cuisine véhicule',
  BATHROOM_AREA_PICTURE: 'Espace douche véhicule',
  CAMPER_VAN_HOLD_PICTURE: 'Ancienne photos de Camper van',
  REAR_ROW_PICTURE: 'Photos de la rangée arrière',
  FRONT_ROW_PICTURE: "Photos de la rangée d'avant",
  HANDLEBAR_PICTURE: 'Guidon',
  DRIVER_SEAT_PICTURE: 'Siège du chauffeur',
  FUEL_TANK_PICTURE: 'Réservoir de carburant',
  PASSENGER_SEAT_PICTURE: 'Siège passager',
  FRONT_WHEEL_RS_PICTURE: 'Roue avant droite',
  REAR_WHEEL_RS_PICTURE: 'Roue arrière droite',
  FRONT_WHEEL_LS_PICTURE: 'Roue avant gauche',
  REAR_TIRE_WEAR_PICTURE: 'Usure des pneus arrière',
  FRONT_TIRE_WEAR_PICTURE: 'Usure des pneux avant',
  CHARGING_CABLE_PICTURE: 'Câble de chargement',
  RIGHT_ROCKER_PANEL: 'Panneau de bas de caisse droit',
  LEFT_DOORS: 'Porte gauche',
  FRONT_LEFT_WING_CONDITION: 'Aile avant gauche',
  RIGHT_REARVIEW_MIRROR_CONDITION: 'Rétroviseur droit',
  CONDITION_WING_REAR_LEFT: 'Aile arrière gauche',
  CONDITION_SIDE_WINDOWS_RIGHT: 'Vitres latérales droites',
  REAR_LEFT_WING: 'Aile arrière gauche',
  CONDITION_DOORS_RIGHT_REAR: 'Porte arrières droites',
  CONDITION_WHEEL_FR: 'Etat des roues',
  FRONT_SEATS_CONDITION: 'Siège avant',
  CONDITION_CARPETS_REAR: "Tapis à l'arrière",
  CONDITION_WINDSHIELD: 'Pare-brise',
  CONDITION_REARVIEW_MIRROR_LEFT: 'Rétroviseur gauche',
  CONDITION_WING_FRONT_RIGHT: 'Aile avant droite',
  CONDITION_ROCKER_PANEL_RIGHT: 'Bas de bascule droit',
  CONDITION_REARVIEW_MIRROR_RIGHT: 'Rétroviseur droit',
  CONDITION_WHEEL_FL: 'Roue avant gauche',
  NEXT_SERVICE_DASHBOARD_PICTURE: 'Tableau de bord du service',
  MILEAGE_PICTURE_TEST_DRIVE_AFTER: 'Kilométrage essaie routier après',
  CONDITION_DASHBOARD: 'Conditions du tableaux de bord',
  CONDITION_RIM_FRONT: 'Etat Jante avant',
  CONDITION_BUMPER_REAR: 'Etat pare-choc arrière',
  CONDITION_DOOR_PANELS_FRONT: 'Etat des panneaux de portes avant',
  REAR_BUMPER_CONDITION: 'Etat du pare-choc arrère_2',
  FRONT_BUMPER_CONDITION: 'Etat du pare-choc avant',
  FRONT_RIM_CONDITION: 'Etat Jante avant_2',
  FRONT_DOOR_PANELS_CONDITION: 'Etat des panneaux de portes avant_2',
  REAR_RIM_CONDITION: 'Etat Jante arrière',
  CONDITION_HEADLINER: 'Etat Headliner',
  CONDITION_WHEEL_RL: 'Etat roue arrière gauche',
  CONDITION_WHEEL_RR: 'Etat roue arrière droite',
  CONDITION_WINDSCREEN_WIPERS_FRONT: 'Etat essuie glace avant',
  TRUNK_CONDITION: 'Etat du coffre',
  DASHBOARD_CONDITION: 'Conditions du tableaux de bord_2',
  UNDERBODY_FLUID_LEAKAGE: 'Fuite de liquide sous caisse',
  RUST: 'Rouiller',
  REPAIR_MARKS: 'Marque de réparation',
  ROOF_CONDITION: 'Etat du toit',
  REAR_WINDOW_CONDITION: 'Etat vitre arrière',
  TRUNK_BODY_CONDITION: 'Etat du coffre_2',
  REAR_RIGHT_WING: 'Aile arrière droite',
  RIGHT_DOORS: 'Porte droite',
  CONDITION_SEATS_FRONT: 'Etat des sièges avant',
  CONDITION_WING_REAR_RIGHT: 'Etat ailes arrière droite',
  ODOMETER_BLOCK: "Bloqueur d'odomètre",
  REAR_SEATS_CONDITION: 'Etat des sièges arrière',
  FRONT_TIRE_CONDITION: 'Etat des pneus avant',
  LEFT_REARVIEW_MIRROR_CONDITION: 'Etat du rétroviseur gauche',
  FRONT_RIGHT_WING_CONDITION: 'Etat ailes arrière droite_2',
  BONNET_CONDITION: 'Etat du capot',
  REAR_LEFT_LIGHT: 'Feu arrière gauche',
  HEADLINER_CONDITION: 'Etat Headliner_2',
  REAR_DOOR_PANELS_CONDITION: 'Etat des panneaux de portes arrière',
  REAR_RIGHT_LIGHT: 'Feu arrière droite',
  WINDSHIELD_CONDITION: 'Etat des pare-brise',
  CONDITION_CARPETS_FRONT: 'Etat des tapis avant',
  CONDITION_STEERING_WHEEL: 'Etat Volant',
  CONDITION_RIM_REAR: 'Etat Jante arrière_2',
  CONDITION_TIRE_FRONT: 'Etat pneu avant',
  CONDITION_TRUNK_BODY: 'Etat carrosserie',
  CONDITION_DOORS_RIGHT_FRONT: 'Etat porte avant droite',
  CONDITION_ROCKER_PANEL_LEFT: 'Etat base de caisse gauche',
  CONDITION_SEATS_REAR: 'Etat siège arrière',
  CONDITION_WING_FRONT_LEFT: 'Etat aile avant gauche',
  CONDITION_TAILLIGHT_RIGHT: 'Etat feu arrière droit',
  CONDITION_DOORS_LEFT_FRONT: 'Etat porte avant gauche',
  PAINT_THICKNESS_WING_REAR_LEFT: 'Epaisseur de peinture aile arr. gauche',
  PAINT_THICKNESS_DOOR_REAR_LEFT: 'Epaisseur de peinture porte arr. gauche',
  PAINT_THICKNESS_DOOR_FRONT_LEFT: 'Epaisseur de peinture porte av. gauche',
  PAINT_THICKNESS_WING_FRONT_LEFT: 'Epaisseur de peintaure aile av. gauche',
  CONDITION_ROOF: 'Etat du toit_2',
  CONDITION_BONNET: 'Etat capot_2',
  CONDITION_BACK_SHELF: "Etat de l'étagère arrière",
  CONDITION_TIRE_REAR: 'Etat pneu arrière_2',
  ODOMETER_BLOCK_WARNING_PRESENCE:
    "Avertissement de blocage du compteur d'odomètre",
  FRONT_CARPETS_CONDITION: 'Etat des tapis avant_2',
  STEERING_WHEEL_CONDITION: 'Etat du volant_2',
  CONDITION_DOOR_PANELS_REAR: 'Etat des panneaux de porte arr_2',
  CONDITION_BLINKER_LEFT: 'Etat clignotant gauche',
  CONDITION_BLINKER_RIGHT: 'Etat clignotant droite',
  CONDITION_HEADLIGHT_RIGHT: 'Etat phare droit',
  REAR_CARPETS_CONDITION: 'Etat des tapis arrière',
  REAR_TIRE_CONDITION: 'Etat pneu arrière_3',
  CONDITION_ENGINE_COMPARTMENT: 'Etat du compartiment de moteur',
  CONDITION_TRUNK: 'Etat du coffre_2',
  CONDITION_HEADLIGHT_LEFT: 'Etat phare gauche',
  CONDITION_EXHAUST: "Etat d'échappement",
  BACK_SHELF_CONDITION: "Etat de l'étagère arrière_2",
};

export const DEFECT_COMPONENT_KEY = {
  SCRATCHED: 'Rayé',
  ASPECT_DEFAULT: "Défault dans l'aspect",
  PAINT_CHIP: 'Eclat de peinture',
  PEELING_PAINT: 'Peinture éclatés',
  TO_BE_REPLACED: 'A remplacer',
  DEFECT_OTHER: 'Autres',
  WORN_OUT: 'Epuisé',
  DEFECT_PEELING_PAINT: 'Défaut de peinture écaillée',
  DEFECT_CHIP: 'Puce défectueuse',
  DEFECT_STAIN: 'Taches',
  DEFECT_CRACKED_GLASS: 'Vitre fissuré',
  DEFECT_WARNING_OTHER: 'Témoin des autres défauts',
  DEFECT_SCRATCH: 'Raillure défectueux',
  DEFECT_BROKEN: 'Cassure défectueux',
  DEFECT_MISSING_ELEMENT: 'Element manquant',
  DEFECT_WORN_OUT: 'Epuisé_2',
  DEFECT_DENT: 'Bosse défectueuse',
  DEFECT_ASPECT_DEFAULT: "Défault dans l'aspect",
  BROKEN: 'Cassé_2',
  HERNIA: 'Hernie',
  PAINT_TRACES: 'Traces de peintures',
  DEFECT_TRACES_OF_HUMIDITY: "Trace d'humidité défectueuse",
  DEFECT_NOT_PROPERLY_FIXED: 'Defaut non correctement réparé',
  OTHER: 'Autre',
  DEFECT_WARNING_ENGINE: 'Témoin de moteur défectueuse',
  DEFECT_TORN: 'Déchiré',
  OTHER_WARNING: 'Autre avertissement',
  DEFECT_HAIL_DAMAGE: 'Dégât de grêle',
  DEFECT_SELLER_REFUSAL: 'Défaut refus du vendeur',
  ASYMMETRY: 'Asymétrie',
  DEFECT_OPAQUE: 'Défaut opaque',
  DEFECT_CRACKED_RUBBER: 'Caoutchou fissuré',
  DEFECT_HERNIA: 'Hernie défectueuse',
  DEFECT_HOLE: 'Trou défectueux',
  DEFECT_CORROSION: 'Corrosion',
  MISALIGNMENT: 'Non alignés',
  SURFACE_CORROSION: 'Corrosion de surface',
  TORN: 'Déchirure',
  ENGINE_WARNING: 'Avertissement moteur',
  DEFECT_CRACKED_LEATHER: 'Cuir fissuré',
  CRACKED: 'Fissuré',
  DEFECT_WARNING_SERVICE: "Service d'Avertissement défectueuse",
  RUSTY: 'Rouillé',
  DEFECT_PAINTED_SEAL: 'Défaut joint peint',
  OPAQUE: 'Opaque',
  DEFECT_CORROSION_SURFACE: 'Defaut de surface de corrosion',
  DISASSEMBLY_AND_REASSEMBLY_MARKS: 'Repère de démontage et remontage',
  OIL: 'Huile',
  SERVICE_WARNING: 'Service en panne',
  DEFECT_ASYMMETRY: 'Assymétrie défectueux',
  DEFECT_WARNING_AIRBAG: 'Témoin airbag défectueux',
  DEFECT_BODY_PANELS_IRREGULAR_SPACING: 'Panneau de carrosserie défectueux',
  DEFECT_VEHICLE_NOT_ALLOWED_TO_CIRCULATE:
    'Véhicule défectueux non autorisé à circuler',
  DEFECT_DIRTY: 'Sale',
  DEFECT_DEFECT: 'Défectueux',
  DEFECT_SMOKE_WHITE: 'Fumée blanche',
  DEFECT_ENGINE_WARNING: 'Témoin de moteur défectueuse_2',
  COOLANT_WARNING: 'Problème de liquide de refroidissement',
  DEFECT_TO_BE_REPLACED: 'A remplacer',
  DEFECT_UNEVEN_PAINTING: 'Peinture inégale',
  DEFECT_PAINT_TRACES: 'Traces de peinture_2',
  DEFECT_DENT_SCRATCH: 'Bosse rayure',
  AUTO1_TEST_DRIVE_GEARBOX_DEFECT_HIGH_CLUTCH_SLIP_POINT:
    'Défaut de la boite de vitesse',
  AUTO1_TEST_DRIVE_NAVIGATION_SYSTEM_DEFECT_SD_CARD_OR_TELEMATICS_BOX_MI:
    'Défaut carte SD et boitier télématique',
  DEFECT_LEAK_TRACES_OF_FUEL: 'Fuite de trace de carburant',
  DEFECT_DISASSEMBLY_AND_REASSEMBLY_MARKS: 'Marque de montage et de démontage',
  COOLANT: 'Liquide de refroidissement',
  DEFECT_STICKER: "Défaut d'Autocollant",
  INCOHERENT_WEAR: 'Usure incohérente',
  INCOHERENT_DATES_AND_MILEAGES_TRACKING:
    'Suivi des dates kilométriques incohérentes',
  PERFORATING_CORROSION: 'Corrosion par perforation',
  DEFORMATION_MARKS: 'Marques de déformations',
  DEFECT_CRACKED: 'Fissure défectueuse',
  AUTO1_TEST_DRIVE_DEFECT_VEHICLE_DANGEROUS_CONDITION:
    'Essai véhicule, état dangereux',
  DEFECT_WARNING_TIRE_PRESSURE: 'Problème de pression de pneu',
  DEFECT_NOT_ORIGINAL: 'default pas original',
  NOT_IDENTIFIED: 'Non identifier',
  DEFECT_WARNING_LIGHT_LAMP: 'Témoin lampe défectueuse',
  PAINTED_SEAL: 'Joint peint',
  SELLER_REFUSAL: 'Refus du vendeur_2',
  DEFECT_DEFORMATION_MARKS: 'Marque de déformations',
  DEFECT_VEHICLE_MECHANICAL_BREAKDOWN: 'Panne mécanique du véhicule',
  DEFECT_MISALIGNMENT: "Défaut d'alignement",
  DEFECT_GEAR_HARD_TO_CHANGE: 'Engrenage défaut difficile à changer',
  DEFECT_REPAIR_LABEL: "Défaut d'étiquette de réparation",
  AIRBAG_WARNING: 'Défaut airbag',
  DEFECT_RODENT_TRACES: 'Trace de rongeur',
  DEFECT_WARNING_BATTERY: 'Défaut de battery',
  IMPRECISE_DIRECTION: 'Direction imprécise',
  DEFECT_SOUND_OTHER: 'Defaut de bruit',
  DEFECT_LEAK_TRACES_OF_COOLANT:
    'Fuites et traces de liquide de refroidissement',
  DEFECT_FLAT_TIRE: 'Pneu crevé',
  DEFECT_HANDLING_PROBLEM_IMPRECISE_DIRECTION:
    'Problème de traitement des défauts de directions',
  OTHER_SOUND: 'Autre son',
  DEFECT_WARNING_COOLANT: 'Problème de liquide de refroidissement',
  ANTI_GRAVEL_COATING_TRACES: 'Trace de revêtement anti-gravillon',
  DEFECT_LEAK_NOT_IDENTIFIED: 'Fuite non identifiée',
  DEFECT_SOUND_BELT: 'Défaut son du courroie',
  FUEL: 'Carburant',
  DEFECT_INCOHERENT_WEAR: 'Usure incohérente',
  AUTO1_TEST_DRIVE_STEERING_DEFECT_HARD_STEERING:
    'Essaie de direction défectueuse',
  DEFECT_LEAK_TRACES_OF_OIL: "Trace d'huile",
  AUTO1_TEST_DRIVE_NAVIGATION_SYSTEM_DEFECT_SD_CARD_OR_TELEMATICS_BOX_MISSING:
    'Essaie de système de navigation Carte SD ou B. télématique manquant',
};
